import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";
import { API, useAuth } from "../App";
import { formatApiError } from "../lib/utils";
import { SHOW_PAID_PLANS } from "../lib/featureFlags";
import { PromoRedeemCard } from "./PromoRedeemCard";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" }) : "—");

export const SubscriptionPanel = () => {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [sub, setSub] = useState(undefined);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, h] = await Promise.all([
        axios.get(`${API}/my-subscription`, { withCredentials: true }),
        axios.get(`${API}/payments/history`, { withCredentials: true }),
      ]);
      setSub(s.data || null);
      setHistory(h.data || []);
    } catch (e) {
      setSub(null);
      toast.error(formatApiError(e, "Could not load billing"));
    }
  };

  useEffect(() => { load(); }, []);

  const act = async (path) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/subscriptions/${path}`, {}, { withCredentials: true });
      toast.success(r.data.message);
      await Promise.all([load(), checkAuth()]);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (sub === undefined) return <div className="h-40 bg-zinc-900 skeleton" />;

  return (
    <div className="space-y-6" data-testid="billing-panel">
      <PromoRedeemCard />
      <div className="bg-zinc-900 border border-zinc-800 p-6">
        <h3 className="font-heading text-xl text-white mb-4">Your Plan</h3>
        {!sub ? (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="billing-free-plan">
            <p className="text-zinc-400" data-testid="billing-plan-label">
              {user?.promo_active
                ? <>You're on the <span className="text-white">Pro</span> plan — free with your promo code.</>
                : <>You're on the <span className="text-white">Free</span> plan.</>}
            </p>
            {SHOW_PAID_PLANS && !user?.promo_active && <Button onClick={() => navigate("/pricing")} className="bg-orange-600 hover:bg-orange-500" data-testid="billing-upgrade-btn">Upgrade plan</Button>}
          </div>
        ) : (
          <div className="space-y-4">
            {sub.status === "past_due" && (
              <div className="flex gap-3 p-3 border border-red-800 bg-red-900/20 text-red-300 text-sm" data-testid="billing-past-due-alert">
                <AlertTriangle className="h-5 w-5 shrink-0" />
                <span>Your last payment failed. We'll retry automatically — or <button className="underline" onClick={() => navigate("/pricing")}>subscribe again with another card</button>.</span>
              </div>
            )}
            <div className="grid sm:grid-cols-3 gap-4">
              <Info label="Plan" value={`${sub.plan_name} · R${sub.amount}/month`} testId="billing-plan-name" />
              <Info label={sub.cancel_at_period_end ? "Active until" : "Next payment"} value={fmt(sub.current_period_end)} testId="billing-next-date" />
              <Info label="Card" value={sub.card_last4 ? `${sub.card_brand || "Card"} •••• ${sub.card_last4}` : "Saved card"} testId="billing-card" />
            </div>
            {sub.cancel_at_period_end ? (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2">
                <p className="text-sm text-yellow-400" data-testid="billing-cancelled-note">Cancelled — you won't be charged again.</p>
                <Button variant="outline" className="border-zinc-700" disabled={busy} onClick={() => act("resume")} data-testid="billing-resume-btn">Keep my plan</Button>
              </div>
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:text-red-400" disabled={busy} data-testid="billing-cancel-btn">Cancel subscription</Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-zinc-950 border-zinc-800">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-white">Cancel your {sub.plan_name} plan?</AlertDialogTitle>
                    <AlertDialogDescription>You won't be charged again. Your benefits stay active until {fmt(sub.current_period_end)}, then your profile moves to the Free plan.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="border-zinc-700" data-testid="billing-cancel-dismiss-btn">Keep plan</AlertDialogCancel>
                    <AlertDialogAction className="bg-red-700 hover:bg-red-600" onClick={() => act("cancel")} data-testid="billing-cancel-confirm-btn">Cancel subscription</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        )}
      </div>
      <div className="bg-zinc-900 border border-zinc-800 p-6">
        <h3 className="font-heading text-xl text-white mb-4 flex items-center gap-2"><CreditCard className="h-5 w-5 text-orange-500" /> Payment History</h3>
        {history.length === 0 ? (
          <p className="text-zinc-500 text-sm" data-testid="billing-history-empty">No payments yet.</p>
        ) : (
          <div className="divide-y divide-zinc-800" data-testid="billing-history">
            {history.map((p, i) => (
              <div key={i} className="flex items-center justify-between py-2 text-sm">
                <span className="text-zinc-300">{p.description}</span>
                <span className="text-zinc-500">{fmt(p.created_at)}</span>
                <span className={p.status === "paid" ? "text-emerald-400" : "text-red-400"}>R{Number(p.amount).toFixed(2)} · {p.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Info = ({ label, value, testId }) => (
  <div>
    <p className="text-xs uppercase tracking-wider text-zinc-500">{label}</p>
    <p className="text-white mt-1" data-testid={testId}>{value}</p>
  </div>
);
